// src/pages/personal/settings/PromotionsDiscountsSection/PromotionsDiscountsSection.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import { api } from '@/services/api';
import { TEXT_STYLES } from '@/theme/typography';
import { COMPONENT_STYLES } from '@/theme/components';

import ClientStatusRules from './ClientStatusRules';
import PurchaseBonus from './PurchaseBonus';
import PromoCodes from './PromoCodes';
import SeasonalEvents from './SeasonalEvents';
import Message from '@/components/ui/Message';
import MessageContainer from '@/components/ui/MessageContainer';
import useMessage from '@/hooks/useMessage';

import { PromotionsDiscountsSettings, PromotionsDiscountsState } from './types';

// Импортируем utility функции и расширяем интерфейс AuthUser
import { hasChanges, initializeSettings } from '../SettingsSection/settingsUtils';

// Расширение типа AuthUser для типизации cost_management
declare module '@/context/AuthContext' {
  interface AuthUser {
    cost_management?: {
      client_status_rules?: any;
      purchase_bonus?: any;
      promo_codes?: any;
      seasonal_events?: any;
      [key: string]: any;
    };
  }
}

const defaultSettings: PromotionsDiscountsSettings = {
  client_status_rules: {
    cold: { discount: { type: 'percentage', value: 0 }, loyalty_bonus: { type: 'percentage', value: 0 } },
    rare: { discount: { type: 'percentage', value: 0 }, loyalty_bonus: { type: 'percentage', value: 0 } },
    new: { discount: { type: 'percentage', value: 0 }, loyalty_bonus: { type: 'percentage', value: 0 } },
    regular: { discount: { type: 'percentage', value: 0 }, loyalty_bonus: { type: 'percentage', value: 0 } },
    loyal: { discount: { type: 'percentage', value: 0 }, loyalty_bonus: { type: 'percentage', value: 0 } },
    lost: { discount: { type: 'percentage', value: 0 }, loyalty_bonus: { type: 'percentage', value: 0 } }
  },
  purchase_bonus: {
    type: 'percentage',
    value: 0
  },
  promo_codes: {},
  seasonal_events: {}
};

const PromotionsDiscountsSection: React.FC = () => {
  // ИСПРАВЛЕНО: Указываем используемые пространства имен
  const { t } = useTranslation(['promotions', 'common']);
  const { user, checkAuth } = useAuthContext();
  const { message, showMessage, hideMessage, isVisible } = useMessage();
  
  const [state, setState] = useState<PromotionsDiscountsState>({
    settings: { ...defaultSettings },
    originalSettings: { ...defaultSettings },
    loading: true,
    saving: false,
    success: false,
    error: ''
  });

  useEffect(() => {
    if (user) {
      // Используем безопасный доступ к cost_management через опциональную цепочку
      // @ts-ignore - игнорируем ошибки TypeScript для cost_management
      const costManagement = user?.cost_management || {};
      const initialSettings = initializeSettings(costManagement, defaultSettings);
      
      setState((prev: PromotionsDiscountsState) => ({
        ...prev,
        settings: initialSettings,
        originalSettings: initialSettings,
        loading: false
      }));
    }
  }, [user]);

  // Функция для обновления статусов клиентов
  const handleClientStatusUpdate = (statusData: any) => {
    setState((prev: PromotionsDiscountsState) => ({
      ...prev,
      settings: {
        ...prev.settings,
        client_status_rules: {
          ...prev.settings.client_status_rules,
          ...statusData
        }
      }
    }));
  };

  // Функция для обновления бонуса за покупку
  const handlePurchaseBonusUpdate = (bonusData: any) => {
    setState((prev: PromotionsDiscountsState) => ({
      ...prev,
      settings: {
        ...prev.settings,
        purchase_bonus: bonusData
      }
    }));
  };

  // Функция для обновления промокодов
  const handlePromoCodesUpdate = (promoCodesData: any) => {
    setState((prev: PromotionsDiscountsState) => ({
      ...prev,
      settings: {
        ...prev.settings,
        promo_codes: promoCodesData
      }
    }));
  };

  // Функция для обновления сезонных событий
  const handleSeasonalEventsUpdate = (eventsData: any) => {
    setState((prev: PromotionsDiscountsState) => ({
      ...prev,
      settings: {
        ...prev.settings,
        seasonal_events: eventsData
      }
    }));
  };
  
  // Обрабатываем данные из API
  const handleSaveSettings = async () => {
    // Проверяем, есть ли изменения
    if (!hasChanges(state.settings, state.originalSettings)) {
      return;
    }
    
    if (!user || !user.id) return;
    
    try {
      setState((prev: PromotionsDiscountsState) => ({ 
        ...prev, 
        saving: true,
        error: '',
        success: false
      }));
      
      // Отправляем запрос на обновление профиля с cost_management
      const response = await api.post(api.endpoints.auth.updateProfile, {
        cost_management: state.settings
      });
      
      if (response && response.success) {
        // Обновляем данные пользователя через контекст аутентификации
        await checkAuth();
        
        // Получаем обновленный профиль
        const updatedProfile = await api.get(api.endpoints.auth.profile);
        
        // Обновляем настройки с данными с сервера
        // @ts-ignore - игнорируем ошибки TypeScript для cost_management
        const updatedSettings = updatedProfile?.cost_management || state.settings;
        
        setState((prev: PromotionsDiscountsState) => ({
          ...prev,
          settings: updatedSettings,
          originalSettings: updatedSettings,
          saving: false,
          success: true
        }));
        
        // Показываем сообщение об успехе
        showMessage('success', null, {
          translateKey: 'promotions:profile.settings_saved',
          autoHide: true,
          autoHideTime: 3000
        });
      } else {
        throw new Error(response.error || 'Ошибка сохранения настроек');
      }
    } catch (err: any) {
      console.error('Error saving promotions settings:', err);
      setState((prev: PromotionsDiscountsState) => ({
        ...prev,
        saving: false,
        error: err.message || t('promotions:profile.settings_save_error')
      }));
      
      // Показываем сообщение об ошибке
      showMessage('error', err.message || t('promotions:profile.settings_save_error'));
    }
  };
  
  if (state.loading) {
    return <Message type="info" translateKey="common:common.loading" icon={true} />;
  }
  
  return (
    <div className={COMPONENT_STYLES.card.base}>
      <div className={COMPONENT_STYLES.card.header}>
        <h2 className={TEXT_STYLES.h2}>{t('promotions:profile.promotions_discounts')}</h2>
      </div>
      
      <div className={COMPONENT_STYLES.card.body}>
        <div className="space-y-6">
          {/* Секция правил статусов клиентов */}
          <ClientStatusRules 
            clientStatusRules={state.settings.client_status_rules} 
            onChange={handleClientStatusUpdate} 
          />
          
          {/* Секция бонуса за покупку */}
          <PurchaseBonus 
            purchaseBonus={state.settings.purchase_bonus} 
            onChange={handlePurchaseBonusUpdate} 
          />
          
          {/* Секция промокодов */}
          <PromoCodes 
            promoCodes={state.settings.promo_codes} 
            onChange={handlePromoCodesUpdate} 
          />
          
          {/* Секция сезонных событий */}
          <SeasonalEvents 
            seasonalEvents={state.settings.seasonal_events} 
            onChange={handleSeasonalEventsUpdate} 
          />
          
          {state.success && (
            <Message 
              type="success" 
              translateKey="promotions:profile.settings_saved"
            />
          )}
          
          {state.error && (
            <Message 
              type="error" 
              message={state.error}
            />
          )}
          
          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={state.saving || !hasChanges(state.settings, state.originalSettings)}
              className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.sizes.md} ${COMPONENT_STYLES.button.primary}
                ${(!hasChanges(state.settings, state.originalSettings) || state.saving) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {state.saving ? t('common:loading') : t('common:save')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Всплывающее сообщение */}
      <MessageContainer
        type={message.type}
        message={message.content}
        translateKey={message.translateKey}
        values={message.values}
        isVisible={isVisible}
        onClose={hideMessage}
        position="top"
      />
    </div>
  );
};

export default PromotionsDiscountsSection;